import * as Sentry from "@sentry/react";
import { DeleteModal } from "components/Dialog";
import { AlertSakeBar } from "components/UI";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import { get } from "lodash";
import React, { memo, useEffect, useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useCurrentClass } from "store/classes/selectors";
import { useCognets } from "store/cogantes/selectors";
import {
  handleAddAndUpdateVocabularyCogantes,
  handleDeleteVocabularyCogantes,
  handleGenerateVocabularyCogantes,
} from "store/cogantes/thunks";
import { useExams } from "store/exams/selectors";
import { handleAddInappropriateAlert } from "store/logs/thunks";
import { useCurrentUser } from "store/users/selectors";
import { checkInappropriateContent } from "utils/methods/word_filter";
import CogantesTable from "./cognatesTable";

const Cogantes = ({
  bookId,
  pageId,
  pageNumber,
  stopAudio = () => {},
  refs = { vocabdomRef: null },
  tabSelector,
  non_native = false,
  handleCheckboxChangeForCogantes = () => {},
  selectedWordCogantes = [],
  setSelectedWordCogantes = () => {},
  studentCognates,
  vocLoader,
  isCognatesAllowedForBookGroup
}) => {
  const showAlert = useAlert();
  const { vocabdomRef } = refs;
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  const bookVocabulary = useCognets();
  const exams = useExams();
  const [getBooksVocab, setGetBooksVocab] = useState([]);
  const [addVocabData, setAddVocabData] = useState({});
  const [vocabularyAI, setVocabularyAI] = useState();
  const [showWordLoader, setShowWordLoader] = useState(false);
  const [showWordEnglishLoader, setShowEnglishWordLoader] = useState(false);
  const [alertSakeBar, setAlertSakeBar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const dispatch = useDispatch();
  const [newItem, setNewItem] = useState({ isEditable: true });
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState({
    type: "",
    value: false,
    selectedData: {},
  });
  const [studentVocab, setStudentVocab] = useState([]);
  const isExam = useMemo(
    () =>
      exams
        ?.flatMap((entry) => entry.exams.map((exam) => `${exam.bookId}`))
        .includes(`${bookId}`),
    [exams]
  );

  const handleAddVocData = (key, value) => {
    if (key == "word") {
      const v = addVocabData;
      addVocabData[key] = value;
      setAddVocabData(v);
    } else {
      const v = vocabularyAI;
      vocabularyAI[key] = value;
      setVocabularyAI(v);
    }
  };

  useEffect(() => {
    try {
      if (tabSelector?.type === 'my_student_cog' || tabSelector?.type === 'unit_cognates') return;
      if (currentUser.role == "student") {
        const filterVocab = bookVocabulary?.filter(
          (obj) =>
            obj?.page == pageId &&
            obj?.created_by == "student" &&
            obj?.user_id == currentUser?.id
        );
        setStudentVocab(filterVocab);
      }

      const filterVocab = bookVocabulary?.filter((obj) => obj?.page == pageId);

      const filterByAdmin =
        filterVocab?.filter((data) => data?.created_by === "owe_admin") || [];
      const filterByTeacher =
        filterVocab?.filter((data) => data?.created_by === "teacher") || [];
      const data = [];
      if (filterByAdmin?.length > 0) data.push(...filterByAdmin);
      if (filterByTeacher?.length > 0) data.push(...filterByTeacher);
      setSelectedWordCogantes(data || []);
      setGetBooksVocab(data || []);
    } catch (error) {
      Sentry.captureException(`338 Error in Cogantes: ${error}`);
      console.log(error);
    }
  }, [bookVocabulary, bookId, pageId, tabSelector]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const selectedModeData = useMemo(() => {
    switch (tabSelector?.type) {
      case "my_cogants":
        return studentVocab;
      case "congantes":
        return getBooksVocab;
      case "unit_cognates":
      case "my_student_cog":
        return studentCognates;
      default:
        return getBooksVocab;
    }
  }, [tabSelector?.type, studentVocab, getBooksVocab, studentCognates]);

  const paginatedData = useMemo(() => {
    return selectedModeData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [selectedModeData, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [tabSelector?.type]);

  function editDataProperty(targetId, newValue, propertyName) {
    const updatedData = selectedModeData.map((item) =>
      item.id === targetId
        ? {
            ...item,
            data: {
              ...item.data,
              [propertyName]: newValue,
            },
          }
        : item
    );

    if (tabSelector?.type === "my_cogants") {
      setStudentVocab(updatedData);
    } else {
      setGetBooksVocab(updatedData);
    }
  }

  const callVocAI = async (type = "") => {
    try {
      if (addVocabData?.word) {
        if (type === "english") {
          setShowEnglishWordLoader(true);
        } else {
          setShowWordLoader(true);
        }
        const ckeck_word = await checkInappropriateContent(addVocabData?.word, 'vocabulary');
        if (ckeck_word.containsInappropriate) {
          setShowWordLoader(false);
          setShowEnglishWordLoader(false);
          handleAddInappropriateAlert(ckeck_word.foundWords, currentUser, currentClass, 'Cogantes',addVocabData?.word)
          // const warning_text = foundWordsWithSynonyms.length > 0 ? foundWordsWithSynonyms.map(item => `"${item.word}" (use "${item.synonym}" instead)`).join(', ') : foundWords.map(item => `"${item.word}"`).join(', ');
          return showAlert(`This word is not available in the dictionary. Please, try another word.`, "error");
        }

        const result = await dispatch(
          handleGenerateVocabularyCogantes({
            topic: addVocabData?.word,
            book_id: bookId,
            page: pageId,
            chapter: bookId,
            created_by: currentUser?.role,
            user_id: get(currentUser, "id", ""),
            class_id: get(currentClass, "id", ""),
            language: type,
          })
        );

        if (result && !result.error) {
          setAlertSakeBar({
            open: true,
            message: "Created Successfully",
            severity: "success",
          });
          if (vocabdomRef && vocabdomRef.current) {
            vocabdomRef.current.scrollTo(0, vocabdomRef.current.scrollHeight);
          }
        } else {
          setAlertSakeBar({
            open: true,
            message: "No Result Found",
            severity: "error",
          });
        }
        setAddVocabData({});
      }
    } catch (error) {
      Sentry.captureException(`360 Error in Cogantes: ${error}`);
      console.log(error);
    } finally {
      setShowEnglishWordLoader(false);
      setShowWordLoader(false);
    }
  };
  const addNewItem = () => {
    if (tabSelector?.type === "my_cogants") {
      const editableItemIndex = studentVocab.findIndex((item) => item.isEditable);
      if (editableItemIndex !== -1) {
        const pageOfEditableItem = Math.ceil((editableItemIndex + 1) / itemsPerPage);
        setCurrentPage(pageOfEditableItem);
      } else {
        setStudentVocab((prev) => {
          const updated = [...prev, newItem];
          const newItemIndex = updated.length - 1;
          const newPage = Math.ceil((newItemIndex + 1) / itemsPerPage);
          if (newPage > currentPage) {
            setCurrentPage(newPage);
          }
          return updated;
        });
      }
    } else {
      const editableItemIndex = getBooksVocab.findIndex((item) => item.isEditable);
      if (editableItemIndex !== -1) {
        const pageOfEditableItem = Math.ceil((editableItemIndex + 1) / itemsPerPage);
        setCurrentPage(pageOfEditableItem);
      } else {
        setGetBooksVocab((prev) => {
          const updated = [...prev, newItem];
          const newItemIndex = updated.length - 1;
          const newPage = Math.ceil((newItemIndex + 1) / itemsPerPage);
          if (newPage > currentPage) {
            setCurrentPage(newPage);
          }
          return updated;
        });
      }
    }
  };
  const onDeleteVocab = async () => {
    try {
      setLoading((prev) => ({ ...prev, value: true }));
      const itemToDelete = loading?.selectedData;
      const currentDataLength = selectedModeData.length;
      const newDataLength = currentDataLength - 1;
      const maxPageAfterDelete = Math.ceil(newDataLength / itemsPerPage) || 1;
      
      await dispatch(handleDeleteVocabularyCogantes(itemToDelete?.id));
      
      if (currentPage > maxPageAfterDelete) {
        setCurrentPage(maxPageAfterDelete);
      }
      
      setDeleteModal(false);
    } catch (error) {
      Sentry.captureException(`361 Error in Cogantes: ${error}`);
      console.log(error);
    } finally {
      setLoading({});
    }
  };

  const handleUpdateData = async (data, propertyName, prevValueRef) => {
    try {
      const payload = getBooksVocab.find((item) => item.id === data?.id);
      if (prevValueRef.current === payload.data[propertyName]) {
        return;
      }
      await dispatch(handleAddAndUpdateVocabularyCogantes(payload));
      setAlertSakeBar({
        open: true,
        message: "Updated Successfully",
        severity: "success",
      });
    } catch (error) {
      Sentry.captureException(`362 Error in Cogantes: ${error}`);
      console.log(error);
    }
  };
   

  return (
    <>
      <AlertSakeBar
        open={alertSakeBar?.open}
        message={alertSakeBar?.message}
        severity="success"
        onClose={() => setAlertSakeBar({ open: false, message: "" })}
      />
      <DeleteModal
        deleteToggle={deleteModal}
        loading={loading?.type === "delete" && loading?.value === true}
        onDelete={() => {
          onDeleteVocab();
        }}
        setDeleteToggle={setDeleteModal}
        message={`Are you sure you want to remove this ${
          loading?.selectedData?.data &&
          loading?.selectedData?.data?.word_spanish &&
          loading?.selectedData?.data?.language === "spanish"
            ? `"${loading?.selectedData?.data?.word_spanish}"`
            : `"${loading?.selectedData?.data?.word_english}"`
        } cognate?`}
        onClickCancel={() => {
          setLoading({});
        }}
      />
      <CogantesTable
        states={{
          getBooksVocab: paginatedData,
          currentUser,
          showWordLoader,
          showWordEnglishLoader,
          tabSelector,
          non_native,
          isExam,
          selectedWordCogantes,
          vocLoader,
          isCognatesAllowedForBookGroup,
          selectedModeDataLength: selectedModeData.length,
          itemsPerPage,
          currentPage,
          setCurrentPage,
          selectedModeData,
        }}
        funcObj={{
          handleAddVocData,
          callVocAI,
          editDataProperty,
          stopAudio,
          addNewItem,
          setDeleteModal,
          setLoading,
          handleUpdateData,
          handleCheckboxChangeForCogantes,
        }}
        
      />
      
      
    </>
  );
};

export default memo(Cogantes);
